import axios, { AxiosInstance } from 'axios';

import { toQueryString } from '@shared/api/helpers';
import { PaginatedResourceResponse, PartialQuery } from '@shared/api/types';
import { EndpointWithId } from '@shared/api/types/EndpointWithId';
import { ObjectWithId } from '@shared/utils/types';

export const api = new (class Api {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL,
      headers: {
        Accept: 'application/json',
      },
      withCredentials: true,
      withXSRFToken: true,
    });
  }

  get<T>(url: string): Promise<T> {
    return this.client.get(url).then((response) => response.data);
  }

  post<T>(url: string, data: object = {}): Promise<T> {
    return this.client.post(url, data).then((response) => response.data);
  }

  postFormData<T>(url: string, data: FormData): Promise<T> {
    return this.client
      .post(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => response.data);
  }

  put<T>(url: string, data: object = {}): Promise<T> {
    return this.client.put(url, data).then((response) => response.data);
  }

  delete<T>(url: string): Promise<T> {
    return this.client.delete(url).then((response) => response.data);
  }

  createIndex<T>(endpoint: string): (query?: PartialQuery) => Promise<PaginatedResourceResponse<T>> {
    return (query) => this.get(query ? `${endpoint}?${toQueryString(query)}` : endpoint);
  }

  createShow<T>(endpoint: EndpointWithId) {
    return ({ id }: ObjectWithId, query?: PartialQuery): Promise<T> => {
      const url = endpoint.replace(':id', String(id));

      return this.get(query ? `${url}?${toQueryString(query)}` : url);
    };
  }

  createCreate<Data extends object, Resource>(endpoint: string): (data: Data) => Promise<Resource> {
    return (data: Data) => this.post(endpoint, data);
  }

  createUpdate<Data extends object, Resource>(
    endpoint: EndpointWithId
  ): ({ id }: ObjectWithId, data: Data) => Promise<Resource> {
    return ({ id }: ObjectWithId, data: Data) => this.put(endpoint.replace(':id', String(id)), data);
  }

  createDelete(endpoint: EndpointWithId): ({ id }: ObjectWithId) => Promise<void> {
    return ({ id }: ObjectWithId) => this.delete(endpoint.replace(':id', String(id)));
  }

  createTrigger<Resource>(endpoint: EndpointWithId): ({ id }: ObjectWithId) => Promise<Resource> {
    return ({ id }: ObjectWithId) => this.post(endpoint.replace(':id', String(id)));
  }

  createAction<Data extends object, Resource>(
    endpoint: EndpointWithId
  ): ({ id }: ObjectWithId, data: Data) => Promise<Resource> {
    return ({ id }: ObjectWithId, data: Data) => this.post(endpoint.replace(':id', String(id)), data);
  }
})();
